/**
 * The active drag, whatever its family: advancing it with the pointer, and
 * committing it as the shape.
 */

import { type FloorPlanEdgeSegment, type Point, removeSegment, resolveLineRef } from '@kaiizen/kai-floor-plan';
import type { EditorMutableState, Selection } from '../editorState';
import { select } from '../selection';
import { moveChamferTriangle } from './chamferTriangleDrag';
import { moveFreeElement, rotateFreeElement } from './freeElementDrag';
import { movePerpendicularDrag } from './perpendicularDrag';
import { moveSegmentSlide } from './segmentSlide';

type AlcoveSelection = Extract<Selection, { kind: 'alcove' | 'alcoveWall' | 'alcoveCorner' }>;

const selectsAlcove = (selection: Selection): selection is AlcoveSelection =>
  selection.kind === 'alcove' || selection.kind === 'alcoveWall' || selection.kind === 'alcoveCorner';

/**
 * Advance the active gesture. `screen`/`model` are stage-relative pointer
 * points. Every gesture REPLACES the draft with a pure edit of the drag base.
 * A gadget drag (moveFree3D) is advanced by the 3D view through moveFree3D,
 * never by the plan's pointer.
 */
export function moveDrag(state: EditorMutableState, screen: Point, model: Point): void {
  const gesture = state.drag;
  if (!gesture || !state.draftShape) return;
  if (gesture.kind === 'perp') movePerpendicularDrag(state, gesture, screen);
  else if (gesture.kind === 'tri') moveChamferTriangle(state, gesture, model);
  else if (gesture.kind === 'rotateFree') rotateFreeElement(state, gesture, model);
  else if (gesture.kind === 'slide') moveSegmentSlide(state, gesture, model);
  else if (gesture.kind === 'moveFree') moveFreeElement(state, gesture, model);
}

/**
 * Commit the active gesture: the draft (valid by construction, since every
 * move was a clamped catalog edit) becomes the shape, and the selection is
 * re-resolved by position in case segment order changed.
 */
export function endDrag(state: EditorMutableState): void {
  if (!state.drag) return;
  const committed = state.draftShape ?? state.shape;
  state.drag = null;
  state.draftShape = null;

  const selectionBefore = state.selection;
  let alcovePosition: number | null = null;
  if (selectsAlcove(selectionBefore)) {
    alcovePosition = committed[selectionBefore.edge]?.segments[selectionBefore.index]?.position ?? null;
  }
  let segmentPosition: number | null = null;
  if (selectionBefore.kind === 'lineSeg') {
    segmentPosition = resolveLineRef(committed, selectionBefore.ref)?.segments[selectionBefore.si]?.position ?? null;
  }

  state.shape = committed;

  if (alcovePosition !== null && selectsAlcove(selectionBefore)) {
    const index = state.shape[selectionBefore.edge]?.segments.findIndex(
      (segment: FloorPlanEdgeSegment) => Math.abs(segment.position - alcovePosition!) < 1e-6,
    ) ?? -1;
    if (index >= 0 && index !== selectionBefore.index) state.selection = { ...selectionBefore, index };
  }
  if (selectionBefore.kind === 'lineSeg' && segmentPosition !== null) {
    const segments = resolveLineRef(state.shape, selectionBefore.ref)?.segments ?? [];
    const segmentIndex = segments.findIndex(segment => Math.abs(segment.position - segmentPosition!) < 1e-6);
    if (segmentIndex >= 0 && segmentIndex !== selectionBefore.si) state.selection = { ...selectionBefore, si: segmentIndex };
  }
  pruneZeroDepthAlcove(state);
}

/**
 * Resizing an alcove's depth all the way to zero IS deleting it: a zero-depth
 * alcove renders nothing and can't be clicked, but would silently live on in
 * the data. Applied after drag commits and depth chip/panel edits; no
 * confirm needed, undo brings it back.
 */
export function pruneZeroDepthAlcove(state: EditorMutableState): void {
  const selection = state.selection;
  if (!selectsAlcove(selection)) return;
  const segment = state.shape[selection.edge]?.segments[selection.index];
  if (!segment || segment.edgeSegmentType !== 'alcove' || (segment.depth ?? 0) > 1e-9) return;
  state.shape = removeSegment(state.shape, { k: 'pe', edge: selection.edge }, selection.index);
  select(state, { kind: 'none' });
}
